import { useCallback, useEffect, useState } from 'react';
import omit from 'lodash.omit';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { buildService } from '@rd-web-markets/shared/dist/services/service';
const competentProfessionalsApi = buildService('/companies/{id}/competent_professionals', {
  only: ['all', 'delete']
}, {
  only: ['postFormData', 'putFormData']
});
const projectLevelApi = buildService('/claim_project_reports/{id}/competent_professionals', {
  only: ['all', 'delete']
}, {
  only: ['postFormData', 'putFormData']
});
const competentProfessionalProjectApi = buildService('/claim_project_reports/{id}/competent_professional_claim_project_reports', {
  only: ['update']
});
export function useProjectCompetentProfessionalService(companyId, rowData, setData, data, removeEmptyRow, emptyRowId) {
  let selectedProjectId = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : null;
  const [body, setBody] = useState(() => {
    if (!rowData.id) {
      return {
        ...rowData,
        competent_professional_claim_project_reports: rowData.competent_professional_claim_project_reports || [{
          claim_project_report_id: selectedProjectId,
          exclude_from_rnd_report: true,
          review_narrative: false,
          notes: null
        }]
      };
    }
    return {
      ...rowData
    };
  });
  useEffect(() => {
    if (rowData.id) {
      setBody({
        ...rowData
      });
    }
  }, [rowData]);
  const addData = useCallback((response, data) => {
    setData([...data, response]);
    setBody({
      ...rowData
    });
  }, [rowData, setData]);
  const updateData = useCallback((response, documentOnly, data) => {
    const selectedDataIndex = data.findIndex(n => n.id === Number(body.id));
    if (documentOnly) {
      data[selectedDataIndex].document = {
        ...response.document
      };
    } else {
      data[selectedDataIndex] = {
        ...response
      };
    }
    setData([...data]);
  }, [body.id, setData]);
  const saveData = useErrorHandling(useCallback(async body => {
    let fieldValues = omit(body, ['id', 'created_at', 'updated_at', 'claim_project_report_id']);
    const fd = new FormData();
    fd.append('competent_professional', JSON.stringify({
      ...fieldValues
    }));
    let response = null;
    if (selectedProjectId) {
      const competentProfessionalProjectIndex = body.competent_professional_claim_project_reports.findIndex(r => r.claim_project_report_id === Number(selectedProjectId));
      const competentProfessionalProjectBody = body.competent_professional_claim_project_reports[competentProfessionalProjectIndex];
      await competentProfessionalProjectApi.update(selectedProjectId, competentProfessionalProjectBody.id, competentProfessionalProjectBody);
      response = await projectLevelApi.putFormData(selectedProjectId, body.id, fd);
    } else {
      response = await competentProfessionalsApi.putFormData(companyId, body.id, fd);
    }
    updateData(response, false, data);
  }, [selectedProjectId, updateData, data, companyId]));
  const createNewData = useErrorHandling(useCallback(async formData => {
    let fieldValues = omit(body, ['id', 'created_at', 'updated_at', 'claim_project_report_id']);
    formData.append('competent_professional', JSON.stringify({
      ...fieldValues,
      company_id: companyId
    }));
    let response = null;
    if (body.claim_project_report_id) {
      response = await projectLevelApi.postFormData(body.claim_project_report_id, formData);
    } else {
      response = await competentProfessionalsApi.postFormData(companyId, formData);
    }
    addData(response, data);
    removeEmptyRow(emptyRowId);
  }, [body, addData, data, removeEmptyRow, emptyRowId, companyId]));
  const deleteData = useErrorHandling(useCallback(async (body, data, selectedProjectId) => {
    if (selectedProjectId) {
      await projectLevelApi.delete(selectedProjectId, body.id);
    } else {
      await competentProfessionalsApi.delete(companyId, body.id);
    }
    const deletedNameListIndex = data.findIndex(n => n.id === Number(body.id));
    data.splice(deletedNameListIndex, 1);
    setData([...data]);
  }, [companyId, setData]));
  return [saveData, createNewData, deleteData, body, setBody];
}